import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Share2, Instagram, Facebook, Link2, Unlink,
  Send, BarChart3, Check, X, Calendar, Sparkles,
  TrendingUp, Crown, Clock, Settings2, Play, Loader2
} from "lucide-react";

export function SocialAgentDashboard({
  subscription, status, posts, schedule, saving, canPost, canAds,
  onDisconnect, onOpenConnect, onOpenNewPost, onOpenAIPost, onOpenSchedule, onSubscribe
}) {
  return (
    <div className="space-y-6 max-w-6xl" data-testid="social-agent-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              subscription?.status === "trial"
                ? "bg-amber-100 text-amber-700"
                : "bg-emerald-100 text-emerald-700"
            }`}>
              {subscription?.status === "trial" ? "TRIAL" : subscription?.plan_name?.toUpperCase()}
            </span>
            {subscription?.expires_at && (
              <span className="text-xs text-muted-foreground">
                Expira em {new Date(subscription.expires_at).toLocaleDateString("pt-BR")}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-black flex items-center gap-2">
            <Share2 className="w-6 h-6 text-pink-600" />
            Kaiso Social Agent
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {canPost && canAds ? "Posts automáticos + Anúncios inteligentes" :
             canPost ? "Posts automáticos para suas redes" :
             "Anúncios inteligentes para seu restaurante"}
          </p>
        </div>
        <div className="flex gap-2">
          {!status?.connected ? (
            <Button
              onClick={onOpenConnect}
              className="bg-gradient-to-r from-pink-600 to-purple-600"
              data-testid="connect-meta-btn"
            >
              <Link2 className="w-4 h-4 mr-2" /> Conectar Meta
            </Button>
          ) : (
            <Button variant="outline" onClick={onDisconnect} className="text-red-600">
              <Unlink className="w-4 h-4 mr-2" /> Desconectar
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Link2 className="w-4 h-4" />
            <span className="text-xs">Conexão</span>
          </div>
          <p className={`text-lg font-bold ${status?.connected ? "text-emerald-600" : "text-gray-400"}`}>
            {status?.connected ? "Conectado" : "Desconectado"}
          </p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Send className="w-4 h-4" />
            <span className="text-xs">Posts</span>
          </div>
          <p className="text-2xl font-black text-pink-600">{posts.length}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs">Agendamento</span>
          </div>
          <p className={`text-lg font-bold ${schedule?.enabled ? "text-emerald-600" : "text-gray-400"}`}>
            {schedule?.enabled ? "Ativo" : "Inativo"}
          </p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs">Plano</span>
          </div>
          <p className="text-lg font-bold text-purple-600">{subscription?.plan_name}</p>
        </div>
      </div>

      {/* Connected Accounts */}
      {status?.connected && (
        <div className="bg-white border rounded-xl p-5">
          <h3 className="font-bold flex items-center gap-2 mb-4">
            <Link2 className="w-4 h-4 text-pink-600" />
            Contas Conectadas
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {status.pages?.map((page, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Facebook className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{page.name}</p>
                  <p className="text-xs text-muted-foreground">Facebook Page</p>
                </div>
                <Check className="w-5 h-5 text-emerald-600" />
              </div>
            ))}
            {status.pages?.filter(p => p.instagram).map((page, idx) => (
              <div key={`ig-${idx}`} className="flex items-center gap-3 p-3 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
                <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
                  <Instagram className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">@{page.instagram.username}</p>
                  <p className="text-xs text-muted-foreground">{page.instagram.followers_count?.toLocaleString()} seguidores</p>
                </div>
                <Check className="w-5 h-5 text-emerald-600" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {status?.connected && (
        <div className="grid sm:grid-cols-3 gap-4">
          {canPost && (
            <Button
              onClick={onOpenNewPost}
              className="h-auto py-4 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
              data-testid="new-post-btn"
            >
              <div className="flex flex-col items-center gap-2">
                <Send className="w-6 h-6" />
                <span>Criar Post</span>
              </div>
            </Button>
          )}
          {canPost && (
            <Button
              variant="outline"
              className="h-auto py-4 hover:bg-purple-50 hover:border-purple-300"
              onClick={onOpenAIPost}
              data-testid="ai-post-btn"
            >
              <div className="flex flex-col items-center gap-2">
                <Sparkles className="w-6 h-6 text-purple-600" />
                <span>Criar com IA</span>
              </div>
            </Button>
          )}
          {canPost && (
            <Button
              variant="outline"
              className="h-auto py-4 hover:bg-amber-50 hover:border-amber-300"
              onClick={onOpenSchedule}
              data-testid="schedule-btn"
            >
              <div className="flex flex-col items-center gap-2">
                <Clock className="w-6 h-6 text-amber-600" />
                <span>Agendar Posts</span>
              </div>
            </Button>
          )}
          {canAds && (
            <Button
              variant="outline"
              className="h-auto py-4 hover:bg-blue-50 hover:border-blue-300"
              onClick={() => toast.info("Em breve: Campanhas de anúncios!")}
              data-testid="ads-btn"
            >
              <div className="flex flex-col items-center gap-2">
                <BarChart3 className="w-6 h-6 text-blue-600" />
                <span>Criar Anúncio</span>
              </div>
            </Button>
          )}
        </div>
      )}

      {/* Schedule Config Preview */}
      {canPost && schedule?.enabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Play className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-bold text-sm">Agendamento Ativo</p>
                <p className="text-xs text-muted-foreground">
                  Post às {schedule.post_time} | {schedule.story_times?.length || 0} stories/dia
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={onOpenSchedule}>
              <Settings2 className="w-4 h-4 mr-1" /> Editar
            </Button>
          </div>
        </div>
      )}

      {/* Recent Posts */}
      <div className="bg-white border rounded-xl p-5">
        <h3 className="font-bold flex items-center gap-2 mb-4">
          <Send className="w-4 h-4" />
          Posts Recentes
        </h3>
        {posts.length === 0 ? (
          <p className="text-sm text-center text-muted-foreground py-8">
            Nenhum post criado ainda
          </p>
        ) : (
          <div className="space-y-3">
            {posts.map(post => (
              <div key={post.id} className="border rounded-lg p-3" data-testid={`post-${post.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm line-clamp-2">{post.content}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {post.platforms?.includes("facebook") && (
                        <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">Facebook</span>
                      )}
                      {post.platforms?.includes("instagram") && (
                        <span className="text-[10px] px-2 py-0.5 bg-pink-100 text-pink-700 rounded-full">Instagram</span>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(post.created_at).toLocaleDateString("pt-BR")}
                      </span>
                    </div>
                  </div>
                  {post.results?.some(r => r.success) ? (
                    <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" /> Publicado
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 rounded-full flex items-center gap-1">
                      <X className="w-3 h-3" /> Erro
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upgrade CTA */}
      {subscription?.plan === "basic" && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold flex items-center gap-2">
                <Crown className="w-5 h-5" /> Upgrade para Premium
              </h3>
              <p className="text-sm text-white/80 mt-1">
                Desbloqueie anúncios inteligentes e insights detalhados
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => onSubscribe("premium")}
              className="bg-white text-purple-700 hover:bg-white/90"
            >
              Fazer Upgrade
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
